import { ref, computed, onUnmounted, watch, Ref, reactive } from "vue";
import { useMutation, useQuery, useApolloClient } from "@vue/apollo-composable";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import {
  GET_TIMERS,
  GET_THEMES,
  GET_TIMESHEET_ROLES,
} from "@/graphql/timesheets/queries";
import {
  CREATE_TIMER,
  STOP_TIMER,
  RESTART_TIMER,
  DELETE_TIMER,
  CREATE_TIMESHEET,
  UPDATE_TIMER_ROLE,
} from "@/graphql/timesheets/mutations";
import { formatDuration } from "@/helpers/dates";
import { Timer, Timesheet } from "@/helpers/client/typesDefinition";
import { Theme, ProjectRole } from "@/helpers/project/typesDefinition";

// localStorage key for tracking the active timer session
const ACTIVE_TIMER_ID_KEY = "fides:activeTimerId";

// Helper functions for localStorage (exported for use in other components)
export const setActiveTimerId = (timerId: string | null) => {
  if (timerId) {
    localStorage.setItem(ACTIVE_TIMER_ID_KEY, timerId);
  } else {
    localStorage.removeItem(ACTIVE_TIMER_ID_KEY);
  }
};

export const getActiveTimerId = (): string | null => {
  return localStorage.getItem(ACTIVE_TIMER_ID_KEY);
};

// Simplified timer period type (don't use GraphQL type which requires __typename)
export interface TimerPeriodState {
  id: string;
  startTime?: Date;
  stopTime?: Date;
  duration?: number;
}

export interface ExtendedTimerState {
  id?: string;
  date: Date;
  theme?: Theme;
  role?: ProjectRole;
  periods: TimerPeriodState[];
  running: boolean;
  displayedValue: string;
  intervalNb?: ReturnType<typeof setInterval>;
  notes?: string;
  activityId?: string;
}

export interface UseTimerAssistantOptions {
  userId: Ref<string>;
  isEmployee?: Ref<boolean>;
}

export interface UseTimerAssistantReturn {
  // State
  currentTimer: Ref<ExtendedTimerState | null>;
  isExpanded: Ref<boolean>;
  isCreatingTimesheet: Ref<boolean>;
  themes: Ref<Theme[]>;
  themesLoading: Readonly<Ref<boolean>>;
  timesheets: Ref<Timesheet[]>;
  availableRoles: Ref<ProjectRole[]>;
  rolesLoading: Readonly<Ref<boolean>>;

  // Computed
  elapsedTime: Readonly<Ref<number>>;
  formattedTime: Readonly<Ref<string>>;

  // Actions
  toggleExpanded: () => void;
  startTimer: (
    theme?: Theme,
    notes?: string,
    activityId?: string,
  ) => Promise<void>;
  stopTimer: () => Promise<void>;
  restartTimer: () => Promise<void>;
  deleteTimer: () => Promise<void>;
  createTimesheet: () => Promise<void>;
  filterThemes: (search: string) => Theme[];
  setSelectedRole: (role: ProjectRole) => void;
}

const elapsedTime = (
  timer: Omit<ExtendedTimerState, "displayedValue" | "intervalNb">,
): number =>
  timer.periods.reduce(
    (acc, period) => acc + (period.duration ?? dayjs().diff(period.startTime)),
    0,
  );

const getDisplayedValue = (
  timer: Omit<ExtendedTimerState, "displayedValue" | "intervalNb">,
): string => formatDuration(elapsedTime(timer));

const startInterval = (timer: ExtendedTimerState) => {
  const runTimer = () => {
    if (timer) {
      timer.displayedValue = getDisplayedValue(timer);
    }
  };
  runTimer();
  timer.intervalNb = setInterval(runTimer, 1000);
};

const stopInterval = (timer: ExtendedTimerState) => {
  if (timer.intervalNb) {
    clearInterval(timer.intervalNb);
    timer.intervalNb = undefined;
  }
};

export function useTimerAssistant(
  options: UseTimerAssistantOptions,
): UseTimerAssistantReturn {
  const toast = useToast();
  const { t } = useI18n();

  const apolloClient = useApolloClient().client;
  const currentTimer = ref<ExtendedTimerState | null>(null);
  const isExpanded = ref(false);
  const isCreatingTimesheet = ref(false);
  const themes = ref<Theme[]>([]);
  const timesheets = ref<Timesheet[]>([]);
  const availableRoles = ref<ProjectRole[]>([]);
  const rolesLoading = ref(false);

  // Method to fetch roles - only called when timer is started
  const fetchProjectRoles = async () => {
    if (!currentTimer.value?.theme?.project?.id) return;

    rolesLoading.value = true;
    try {
      // Format date as YYYY-MM-DD for GraphQL Date scalar
      const dateStr = dayjs(currentTimer.value.date).format("YYYY-MM-DD");

      const variables: { projectId: string; userId: string; date: string } = {
        projectId: currentTimer.value.theme.project.id,
        userId: options.userId.value,
        date: dateStr,
      };

      const { data } = await apolloClient.query({
        query: GET_TIMESHEET_ROLES,
        variables,
      });

      if (data?.timesheetRoles) {
        availableRoles.value = data.timesheetRoles;
        // Auto-select the current timer's role if it exists in the available roles
        if (currentTimer.value?.role?.id) {
          const existingRole = availableRoles.value.find(
            (r) => r.id === currentTimer.value?.role?.id,
          );
          if (existingRole && currentTimer.value) {
            currentTimer.value.role = existingRole;
          }
        } else if (availableRoles.value.length > 0 && currentTimer.value) {
          // Auto-select first role if none is set, and persist it to the backend
          const autoSelectedRole = availableRoles.value[0];
          currentTimer.value.role = autoSelectedRole;
          // Persist the auto-selected role to the backend
          if (currentTimer.value.id && autoSelectedRole.id) {
            await updateTimerRoleMutation({
              id: currentTimer.value.id,
              roleId: autoSelectedRole.id,
            });
          }
        }
      }
    } catch (error) {
      logErrorMessages(error);
    } finally {
      rolesLoading.value = false;
    }
  };

  // Fetch timers on init (only for employees)
  const isEmployeeUser = options.isEmployee ?? ref(true);
  const { onError: onTimersError, onResult: onTimersResult } = useQuery<{
    timers: Timer[];
  }>(GET_TIMERS, undefined, () => ({
    enabled: isEmployeeUser?.value ?? true,
  }));

  onTimersResult((res) => {
    const timers = res.data.timers;
    const activeTimerId = getActiveTimerId();

    // Find the running timer if any
    const runningTimer = timers.find((t) => {
      if (t.periods.length === 0) return false;
      const lastPeriod = t.periods[t.periods.length - 1];
      return !lastPeriod.stopTime;
    });

    if (runningTimer) {
      currentTimer.value = {
        id: runningTimer.id,
        date: runningTimer.date,
        theme: runningTimer.theme as Theme,
        role: runningTimer.role as ProjectRole,
        periods: runningTimer.periods.map((p) => ({
          id: p.id,
          startTime: p.startTime,
          stopTime: p.stopTime,
          duration: p.duration,
        })),
        running: true,
        displayedValue: "",
        notes: runningTimer.notes,
      };
      startInterval(currentTimer.value);
      // Fetch roles for the restored timer
      fetchProjectRoles();
    } else if (activeTimerId) {
      // No running timer, but we have a stored active timer ID
      // Try to find and restore that specific timer
      const storedTimer = timers.find((t) => t.id === activeTimerId);
      if (storedTimer && storedTimer.periods.length > 0) {
        currentTimer.value = {
          id: storedTimer.id,
          date: storedTimer.date,
          theme: storedTimer.theme as Theme,
          role: storedTimer.role as ProjectRole,
          periods: storedTimer.periods.map((p) => ({
            id: p.id,
            startTime: p.startTime,
            stopTime: p.stopTime,
            duration: p.duration,
          })),
          running: false,
          displayedValue: getDisplayedValue({
            id: storedTimer.id,
            date: storedTimer.date,
            theme: storedTimer.theme as Theme,
            role: storedTimer.role as ProjectRole,
            periods: storedTimer.periods.map((p) => ({
              id: p.id,
              startTime: p.startTime,
              stopTime: p.stopTime,
              duration: p.duration,
            })),
            running: false,
          }),
          notes: storedTimer.notes,
        };
        // Fetch roles for the restored paused timer
        fetchProjectRoles();
      } else {
        // Stored timer not found (was deleted or timesheet created), clear the storage
        setActiveTimerId(null);
        currentTimer.value = null;
      }
    }
    // Note: If there's no running timer and no stored activeTimerId, we don't clear currentTimer
    // This allows the timer to persist across page navigations
  });

  onTimersError((error) => {
    logErrorMessages(error);
    toast.error(t("errors.getTimers", { message: error }));
  });

  // Fetch themes (only for employees)
  const {
    result: themesResult,
    loading: themesLoading,
    onError: onThemesError,
  } = useQuery(GET_THEMES, undefined, () => ({
    enabled: isEmployeeUser?.value ?? true,
  }));

  watch(themesResult, (result) => {
    if (result?.themes?.items) {
      themes.value = result.themes.items;
    }
  });

  onThemesError((error) => {
    logErrorMessages(error);
    toast.error(t("errors.getThemes", { message: error }));
  });

  // Mutations
  const { mutate: createTimerMutation } = useMutation(CREATE_TIMER);
  const { mutate: stopTimerMutation } = useMutation(STOP_TIMER);
  const { mutate: restartTimerMutation } = useMutation(RESTART_TIMER);
  const { mutate: deleteTimerMutation } = useMutation(DELETE_TIMER);
  const { mutate: createTimesheetMutation } = useMutation(CREATE_TIMESHEET);
  const { mutate: updateTimerRoleMutation } = useMutation(UPDATE_TIMER_ROLE);

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
  };

  const startTimer = async (
    theme?: Theme,
    notes?: string,
    activityId?: string,
  ) => {
    if (currentTimer.value?.running) {
      await stopTimer();
    }

    const startTime = new Date();
    const result = await createTimerMutation({
      startTime,
      themeId: theme?.id,
      notes,
      activityId,
    });

    const timerData = result?.data?.createTimer;
    if (timerData?.status === "ok" && timerData?.data?.id) {
      const newTimer: ExtendedTimerState = reactive({
        id: timerData.data.id,
        date: startTime,
        theme,
        periods: [
          {
            id: "temp",
            startTime,
            stopTime: undefined,
            duration: undefined,
          },
        ],
        running: true,
        displayedValue: "00:00:00",
        notes,
        activityId,
      });
      currentTimer.value = newTimer;
      // Store the timer ID in localStorage
      setActiveTimerId(timerData.data.id);
      startInterval(newTimer);
      // Fetch roles now that timer has been started
      await fetchProjectRoles();
    } else if (timerData?.error) {
      toast.error(t("errors.createTimer", { message: timerData.error }));
    }
  };

  const stopTimer = async () => {
    if (!currentTimer.value?.id) return;

    const stopTime = new Date();
    await stopTimerMutation({
      id: currentTimer.value.id,
      stopTime,
    });

    // Update the last period with stop time
    if (currentTimer.value.periods.length > 0) {
      const lastPeriod =
        currentTimer.value.periods[currentTimer.value.periods.length - 1];
      lastPeriod.stopTime = stopTime;
      lastPeriod.duration = dayjs(stopTime).diff(lastPeriod.startTime);
    }

    currentTimer.value.running = false;
    stopInterval(currentTimer.value);
  };

  const restartTimer = async () => {
    if (!currentTimer.value?.id) return;

    const startTime = new Date();
    await restartTimerMutation({
      id: currentTimer.value.id,
      restartTime: startTime,
    });

    currentTimer.value.periods.push({
      id: "temp",
      startTime,
      stopTime: undefined,
      duration: undefined,
    });
    currentTimer.value.running = true;
    startInterval(currentTimer.value);
  };

  const createTimesheet = async () => {
    if (!currentTimer.value) return;

    // Calculate elapsed time in minutes, rounded up to 15-min blocks (like timer page)
    const elapsedMs = elapsedTime(currentTimer.value);
    const nb15Min = Math.ceil(elapsedMs / (1000 * 60 * 15));
    const durationInMinutes = nb15Min * 15;

    // Create the timesheet entry with theme, role, notes, and activityId
    if (currentTimer.value.theme?.id) {
      // Format date as YYYY-MM-DD for GraphQL Date scalar
      const dateStr = dayjs(currentTimer.value.date).format("YYYY-MM-DD");
      const timesheetPayload: {
        date: string;
        duration: number;
        userId: string;
        themeId: string;
        roleId?: string;
        notes?: string;
        activityId?: string;
      } = {
        date: dateStr,
        duration: durationInMinutes,
        userId: options.userId.value,
        themeId: currentTimer.value.theme.id,
      };
      if (currentTimer.value.role?.id) {
        timesheetPayload.roleId = currentTimer.value.role.id;
      }
      // Include notes from the timer if they exist
      if (currentTimer.value.notes) {
        timesheetPayload.notes = currentTimer.value.notes;
      }
      // Include activityId from the timer if it exists
      if (currentTimer.value.activityId) {
        timesheetPayload.activityId = currentTimer.value.activityId;
      }
      await createTimesheetMutation(timesheetPayload);
    }

    // Delete the timer
    if (currentTimer.value.id) {
      await deleteTimerMutation({ id: currentTimer.value.id });
    }

    // Clear the active timer ID from localStorage
    setActiveTimerId(null);

    stopInterval(currentTimer.value);
    currentTimer.value = null;
  };

  const deleteTimer = async () => {
    if (!currentTimer.value?.id) return;

    // Delete the timer
    await deleteTimerMutation({ id: currentTimer.value.id });

    // Clear the active timer ID from localStorage
    setActiveTimerId(null);

    stopInterval(currentTimer.value);
    currentTimer.value = null;
  };

  const filterThemes = (search: string): Theme[] => {
    if (!search) return themes.value;
    const lowerSearch = search.toLowerCase();
    return themes.value.filter(
      (theme) =>
        theme.name.toLowerCase().includes(lowerSearch) ||
        theme.project?.name.toLowerCase().includes(lowerSearch),
    );
  };

  const setSelectedRole = async (role: ProjectRole) => {
    if (currentTimer.value) {
      currentTimer.value.role = role;
      // Persist the role to the backend
      if (currentTimer.value.id && role.id) {
        await updateTimerRoleMutation({
          id: currentTimer.value.id,
          roleId: role.id,
        });
      }
    }
  };

  // Cleanup on unmount
  onUnmounted(() => {
    if (currentTimer.value?.intervalNb) {
      clearInterval(currentTimer.value.intervalNb);
    }
  });

  return {
    currentTimer,
    isExpanded,
    isCreatingTimesheet,
    themes,
    themesLoading,
    timesheets,
    availableRoles,
    rolesLoading,
    elapsedTime: computed(() =>
      currentTimer.value ? elapsedTime(currentTimer.value) : 0,
    ),
    formattedTime: computed(() =>
      currentTimer.value ? currentTimer.value.displayedValue : "00:00:00",
    ),
    toggleExpanded,
    startTimer,
    stopTimer,
    restartTimer,
    deleteTimer,
    createTimesheet,
    filterThemes,
    setSelectedRole,
  };
}
